import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { getInvite, markInviteUsed } from "../../services/firestore";
import "./Login.css";

export default function AcceptInvite() {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    async function loadInvite() {
      const inv = await getInvite(inviteId);
      if (!inv || inv.used) {
        setError("This invite link is invalid or has already been used.");
      } else if (new Date() > inv.expiresAt.toDate()) {
        setError("This invite link has expired. Ask your owner to send a new one.");
      } else {
        setInvite(inv);
      }
      setFetching(false);
    }
    loadInvite();
  }, [inviteId]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      return setError("Password must be at least 8 characters.");
    }
    if (password !== confirm) {
      return setError("Passwords do not match.");
    }
    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(auth, invite.email, password);
      await setDoc(doc(db, "users", result.user.uid), {
        name: invite.name,
        email: invite.email,
        role: "guard",
        companyId: invite.companyId,
        profileComplete: false,
        createdAt: serverTimestamp(),
      });
      await markInviteUsed(inviteId);
      navigate("/guard/profile");
    } catch (err) {
      setError("Something went wrong. Please try again or contact your owner.");
    }
    setLoading(false);
  }

  if (fetching) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <div className="login-logo">
            <span className="login-logo-main">VeloceFlow</span>
          </div>
          <p style={{ textAlign: "center", color: "#888", fontSize: 13 }}>Loading your invite...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        <div className="login-logo">
          <span className="login-logo-main">VeloceFlow</span>
          <span className="login-logo-sub">Set up your guard account</span>
        </div>

        {invite && (
          <div style={{
            background: "#EEF4FF", borderRadius: 8, padding: "10px 14px",
            marginBottom: 20, fontSize: 12, color: "#0C3976"
          }}>
            You've been invited to join as a guard. Your name and email are pre-filled.
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="login-error">{error}</div>}

          <div className="login-field">
            <label>Name</label>
            <input type="text" value={invite?.name || ""} readOnly
              style={{ background: "#f5f5f5", color: "#888" }} />
          </div>
          <div className="login-field">
            <label>Email</label>
            <input type="email" value={invite?.email || ""} readOnly
              style={{ background: "#f5f5f5", color: "#888" }} />
          </div>
          <div className="login-field">
            <label>Create password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              required
              disabled={!invite}
            />
          </div>
          <div className="login-field">
            <label>Confirm password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter password"
              required
              disabled={!invite}
            />
          </div>
          <button type="submit" className="login-btn" disabled={loading || !invite}>
            {loading ? "Creating account..." : "Create account →"}
          </button>
        </form>
        <div className="login-footer">veloceflow.net</div>
      </div>
    </div>
  );
}
